import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { Button } from '@/components/Button'
import { Card } from '@/components/Card'
import { cardapioService, CreateCardapioData, UpdateCardapioData } from '@/services/cardapio'
import { ChefHat, Upload, Save, ArrowLeft, Calendar } from 'lucide-react'

export function CardapioFormPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isEdit = !!id

  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    nome_cardapio: '',
    descricao_cardapio: '',
    validade: ''
  })
  const [selectedFile, setSelectedFile] = useState<File | null>(null)

  const loadCardapio = async () => {
    if (!id) return

    try {
      setLoading(true)
      const response = await cardapioService.getById(parseInt(id))
      
      if (response.success && response.data) {
        const cardapio = response.data[0] // A API retorna array
        setFormData({
          nome_cardapio: cardapio.nome_cardapio,
          descricao_cardapio: cardapio.descricao_cardapio,
          validade: cardapio.validade.split('T')[0] // Converte para formato de input date
        })
      } else {
        toast.error('Cardápio não encontrado')
        navigate('/cardapios')
      }
    } catch (error) {
      console.error('Erro ao carregar cardápio:', error)
      toast.error('Erro ao carregar cardápio')
      navigate('/cardapios')
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      // Validar tipo de arquivo (PDF, DOC, DOCX, imagens)
      const allowedTypes = [
        'application/pdf', 
        'application/msword', 
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'image/jpeg',
        'image/png',
        'image/jpg'
      ]
      if (!allowedTypes.includes(file.type)) {
        toast.error('Apenas arquivos PDF, DOC, DOCX ou imagens (JPG, PNG) são permitidos')
        return
      }
      
      // Validar tamanho (máximo 50MB)
      if (file.size > 50 * 1024 * 1024) {
        toast.error('Arquivo muito grande. Máximo permitido: 50MB')
        return
      }
      
      setSelectedFile(file)
    }
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!formData.nome_cardapio.trim()) {
      toast.error('Nome do cardápio é obrigatório')
      return
    }

    if (!formData.descricao_cardapio.trim()) {
      toast.error('Descrição é obrigatória')
      return
    }

    if (!formData.validade) {
      toast.error('Data de validade é obrigatória')
      return
    }

    // Validar se a data de validade não é no passado
    const validadeDate = new Date(formData.validade)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    
    if (validadeDate < today) {
      toast.error('Data de validade não pode ser no passado')
      return
    }

    if (!isEdit && !selectedFile) {
      toast.error('Arquivo é obrigatório para novos cardápios')
      return
    }

    try {
      setSubmitting(true)

      if (isEdit) {
        // Atualizar cardápio existente
        const updateData: UpdateCardapioData = {
          id_cardapio: parseInt(id!),
          nome_cardapio: formData.nome_cardapio,
          descricao_cardapio: formData.descricao_cardapio,
          validade: formData.validade
        }

        const response = await cardapioService.update(updateData)
        
        if (response.success) {
          toast.success('Cardápio atualizado com sucesso!')
          navigate('/cardapios')
        } else {
          toast.error(response.error || 'Erro ao atualizar cardápio')
        }
      } else {
        // Criar novo cardápio
        const createData: CreateCardapioData = {
          nome_cardapio: formData.nome_cardapio,
          descricao_cardapio: formData.descricao_cardapio,
          validade: formData.validade,
          file: selectedFile!
        }

        const response = await cardapioService.create(createData)
        
        if (response.success) {
          toast.success('Cardápio criado com sucesso!')
          navigate('/cardapios')
        } else {
          toast.error(response.error || 'Erro ao criar cardápio')
        }
      }
    } catch (error) {
      console.error('Erro ao salvar:', error)
      toast.error('Erro ao salvar cardápio')
    } finally {
      setSubmitting(false)
    }
  }

  useEffect(() => {
    if (isEdit) {
      loadCardapio()
    }
  }, [id])

  if (loading) {
    return (
      <div className="w-full">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
          <p className="mt-2 text-gray-600">Carregando...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full max-w-4xl mx-auto">
      {/* Cabeçalho da página */}
      <div className="mb-8">
        <div className="flex items-center space-x-4 mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/cardapios')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
        </div>
        
        <div className="flex items-center space-x-4">
          <ChefHat className="w-8 h-8 text-orange-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {isEdit ? 'Editar Cardápio' : 'Novo Cardápio'}
            </h1>
            <p className="text-gray-600">
              {isEdit ? 'Atualizar informações do cardápio' : 'Adicionar novo cardápio'}
            </p>
          </div>
        </div>
      </div>

      {/* Formulário */}
      <Card>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Nome do Cardápio */}
            <div className="md:col-span-2">
              <label htmlFor="nome" className="block text-sm font-medium text-gray-700 mb-2">
                Nome do Cardápio *
              </label>
              <input
                type="text"
                id="nome"
                value={formData.nome_cardapio}
                onChange={(e) => handleInputChange('nome_cardapio', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                placeholder="Ex: Cardápio Semanal - Janeiro 2024"
                required
              />
            </div>

            {/* Data de Validade */}
            <div className="md:col-span-2">
              <label htmlFor="validade" className="block text-sm font-medium text-gray-700 mb-2">
                Data de Validade *
              </label>
              <div className="relative">
                <input
                  type="date"
                  id="validade"
                  value={formData.validade}
                  onChange={(e) => handleInputChange('validade', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                  required
                />
                <Calendar className="absolute right-3 top-2.5 h-5 w-5 text-gray-400 pointer-events-none" />
              </div>
              <p className="mt-1 text-sm text-gray-500">
                Selecione até quando este cardápio será válido
              </p>
            </div>

            {/* Descrição */}
            <div className="md:col-span-2">
              <label htmlFor="descricao" className="block text-sm font-medium text-gray-700 mb-2">
                Descrição *
              </label>
              <textarea
                id="descricao"
                rows={4}
                value={formData.descricao_cardapio}
                onChange={(e) => handleInputChange('descricao_cardapio', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                placeholder="Descrição detalhada do cardápio, incluindo informações sobre pratos, ingredientes, etc."
                required
              />
            </div>

            {/* Upload de Arquivo */}
            {!isEdit && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Arquivo do Cardápio *
                </label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                  <div className="space-y-1 text-center">
                    <Upload className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="flex text-sm text-gray-600">
                      <label
                        htmlFor="file-upload"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-orange-600 hover:text-orange-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-orange-500"
                      >
                        <span>Selecionar arquivo</span>
                        <input
                          id="file-upload"
                          name="file-upload"
                          type="file"
                          className="sr-only"
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          onChange={handleFileChange}
                        />
                      </label>
                      <p className="pl-1">ou arraste e solte</p>
                    </div>
                    <p className="text-xs text-gray-500">
                      PDF, DOC, DOCX, JPG, PNG até 50MB
                    </p>
                    {selectedFile && (
                      <p className="text-sm text-green-600 font-medium">
                        Arquivo selecionado: {selectedFile.name}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Botões de ação */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/cardapios')}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              loading={submitting}
            >
              <Save className="w-4 h-4 mr-2" />
              {isEdit ? 'Atualizar' : 'Salvar'} Cardápio
            </Button>
          </div>
        </form>
      </Card>
    </div>
  )
}
